import { createContext, useContext, useMemo } from 'react'
import type { ReactNode } from 'react'
import type { Dictionary } from '@/i18n/dictionary'
import type { Locale } from '@/i18n/locales'
import { getDictionary } from '@/i18n'
import type { Route, RouteKind } from '@/app/routes'
import { routePath } from '@/app/routes'

interface SiteContextValue {
  locale: Locale
  dictionary: Dictionary
  route: Route
  pathFor: (kind: RouteKind, locale?: Locale) => string
}

const SiteContext = createContext<SiteContextValue | null>(null)

interface SiteProviderProps {
  route: Route
  children: ReactNode
}

export function SiteProvider({ route, children }: SiteProviderProps) {
  const value = useMemo<SiteContextValue>(
    () => ({
      locale: route.locale,
      dictionary: getDictionary(route.locale),
      route,
      pathFor: (kind, locale) => routePath(kind, locale ?? route.locale),
    }),
    [route],
  )

  return <SiteContext.Provider value={value}>{children}</SiteContext.Provider>
}

export function useSite(): SiteContextValue {
  const value = useContext(SiteContext)
  if (value === null) throw new Error('useSite must be used inside SiteProvider')
  return value
}

export function useTranslations(): Dictionary {
  return useSite().dictionary
}
